import { Link, useParams } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { SERVICES, getServiceBySlug } from "@/data/services";
import NotFound from "@/pages/NotFound";

export default function ServicePage() {
  const { slug } = useParams<{ slug: string }>();
  const service = getServiceBySlug(slug);

  if (!service) {
    return <NotFound />;
  }

  const others = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <>
      <PageHero
        eyebrow="What we offer"
        title={service.title}
        intro={service.tagline}
        crumbs={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/#services" },
          { label: service.title },
        ]}
      />

      <section className="bg-cream-50">
        <div className="section grid gap-14 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <p className="text-base leading-relaxed text-sage-700/90 sm:text-lg">
              {service.intro}
            </p>

            <div className="mt-12 grid gap-6 sm:grid-cols-3">
              {service.highlights.map((h) => (
                <div
                  key={h.title}
                  className="rounded-2xl border border-sage-100 bg-cream-100/60 p-6"
                >
                  <h3 className="font-serif text-base text-sage-800">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-sage-700/90">
                    {h.copy}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 space-y-10">
              {service.details.map((section) => (
                <div key={section.heading}>
                  <h2 className="font-serif text-2xl text-sage-800 sm:text-3xl">
                    {section.heading}
                  </h2>
                  <p className="mt-4 text-sage-700/90">{section.copy}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/#contact" className="btn-primary">
                Request a consultation
              </Link>
              <Link to="/#services" className="btn-secondary">
                Back to all services
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-2xl border border-blush-100 bg-blush-50/60 p-6">
                <h3 className="font-serif text-lg text-sage-800">A great fit for</h3>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-sage-700/90">
                  {service.goodFor.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span aria-hidden="true" className="text-blush-400">
                        •
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-sage-100 bg-sage-50/60 p-6">
                <h3 className="font-serif text-lg text-sage-800">
                  Not sure which option is right?
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-sage-700/90">
                  Every smile is different. Schedule a consultation and Dr. Amy
                  will walk you through the approach that fits your goals,
                  lifestyle, and budget.
                </p>
                <Link
                  to="/#contact"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blush-500 hover:gap-2"
                >
                  Book a consultation <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-sage-50/60">
        <div className="section">
          <p className="eyebrow">Other services</p>
          <h2 className="mt-3 font-serif text-2xl text-sage-800 sm:text-3xl">
            Explore more of what we offer.
          </h2>
          <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((other) => (
              <li key={other.slug}>
                <Link
                  to={`/services/${other.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-sage-100 bg-cream-50 p-6 transition hover:-translate-y-1 hover:border-blush-200 hover:shadow-soft"
                >
                  <h3 className="font-serif text-lg text-sage-800">{other.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-sage-700/90">
                    {other.shortDescription}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blush-500 transition group-hover:gap-2">
                    Learn more <span aria-hidden="true">→</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
